import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { addRumError } from '@/integrations/datadog/init';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  resetKey?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static isChunkLoadError(error: Error): boolean {
    return (
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('dynamically imported module') ||
      error.message?.includes('Loading chunk') ||
      error.name === 'ChunkLoadError'
    );
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    addRumError(error, { componentStack: errorInfo.componentStack ?? '' });
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    if (ErrorBoundary.isChunkLoadError(error)) {
      console.warn('[ErrorBoundary] Chunk load error detected — reloading page...');
      window.location.reload();
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Auto-reset when resetKey changes (user navigated to a different route)
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isChunkError = this.state.error
        ? ErrorBoundary.isChunkLoadError(this.state.error)
        : false;

      if (isChunkError) {
        return (
          <div className="min-h-[400px] flex items-center justify-center p-8">
            <div className="flex flex-col items-center gap-4 text-center max-w-md">
              <RefreshCw className="h-8 w-8 text-primary animate-spin" />
              <h2 className="text-xl font-semibold text-foreground">
                Nova versão disponível
              </h2>
              <p className="text-muted-foreground text-sm">
                Uma nova versão da plataforma foi publicada. Recarregando automaticamente...
              </p>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <div className="flex flex-col items-center gap-5 text-center max-w-md">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-foreground">
                Algo deu errado
              </h2>
              <p className="text-muted-foreground text-sm">
                Ocorreu um erro inesperado ao carregar esta página.
              </p>
            </div>
            {this.state.error && (
              <details className="w-full">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                  Detalhes do erro
                </summary>
                <pre className="text-xs text-muted-foreground bg-muted p-3 rounded-md mt-2 max-w-full overflow-auto text-left">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <div className="flex flex-col items-center gap-2 w-full">
              <div className="flex gap-3">
                <Button variant="outline" onClick={this.handleReset}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Tentar novamente
                </Button>
                <Button onClick={() => window.location.href = '/dashboard'}>
                  <Home className="mr-2 h-4 w-4" />
                  Ir para o Dashboard
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Se o problema persistir, entre em contato com o suporte.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
